/**
 * StepClassification - Step 2: Document/Image Classification
 *
 * Allows users to:
 * - Choose between Image or Document type
 * - For Image: Select subtype (Person, Gravestone, Place, Other)
 * - For Document: Select text type and record subtype
 */

import { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import {
  SnippetItem,
  SnippetDraftData,
  SNIPPET_TYPES,
  IMAGE_SUBTYPES,
  TEXT_TYPES,
  getRecordSubtypes,
  RecordSubtype,
} from "../../../api/snippets";
import { useMediaUrl } from "../../../hooks/useMediaUrl";

interface StepClassificationProps {
  snippet: SnippetItem;
  draftData: SnippetDraftData;
  updateDraft: (updates: Partial<SnippetDraftData>) => void;
  imageKey: number;
}

export function StepClassification({
  snippet,
  draftData,
  updateDraft,
  imageKey,
}: StepClassificationProps) {
  const { getAccessTokenSilently } = useAuth0();

  // Image preview
  const { url: imageUrl, isLoading: imageLoading } = useMediaUrl(
    snippet.id,
    snippet.file_size + imageKey
  );

  // Fetch record subtypes
  const { data: recordSubtypes = [] } = useQuery<RecordSubtype[]>({
    queryKey: ["recordSubtypes"],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getRecordSubtypes(token);
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  // Local state for form
  const [snippetType, setSnippetType] = useState<"image" | "document" | null>(
    draftData.snippet_type
  );
  const [imageSubtype, setImageSubtype] = useState<"person" | "gravestone" | "place" | "other" | null>(
    draftData.image_subtype
  );
  const [textType, setTextType] = useState<"printed" | "handwritten" | "combination" | "unknown" | null>(
    draftData.text_type
  );
  const [recordSubtype, setRecordSubtype] = useState<string | null>(
    draftData.record_subtype
  );

  // Clear sub-options when main type changes
  const handleSnippetTypeChange = (type: "image" | "document") => {
    setSnippetType(type);
    if (type === "image") {
      // Clear document-specific fields
      setTextType(null);
      setRecordSubtype(null);
    } else {
      // Clear image-specific fields
      setImageSubtype(null);
    }
  };

  // Update draft when values change
  useEffect(() => {
    updateDraft({
      snippet_type: snippetType,
      image_subtype: imageSubtype,
      text_type: textType,
      record_subtype: recordSubtype,
    });
  }, [snippetType, imageSubtype, textType, recordSubtype, updateDraft]);

  // Icon for image subtypes
  const getImageSubtypeIcon = (value: string) => {
    switch (value) {
      case "person":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
          </svg>
        );
      case "gravestone":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 21h18M5 21V7a2 2 0 012-2h10a2 2 0 012 2v14" />
          </svg>
        );
      case "place":
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
          </svg>
        );
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Image preview */}
      <div className="bg-vault-100 rounded-lg p-4 min-h-[300px] flex items-center justify-center">
        {imageLoading ? (
          <div className="flex flex-col items-center gap-2 text-vault-500">
            <svg className="animate-spin h-8 w-8" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <span>Loading image...</span>
          </div>
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt={snippet.original_filename}
            className="max-w-full max-h-[400px] object-contain rounded shadow-lg"
          />
        ) : (
          <div className="text-vault-400">No image available</div>
        )}
      </div>

      {/* Classification form */}
      <div className="space-y-6">
        {/* Snippet Type */}
        <div>
          <h3 className="text-sm font-semibold text-vault-800 mb-3">
            What type of content is this? <span className="text-red-500">*</span>
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {SNIPPET_TYPES.map((type) => (
              <button
                key={type.value}
                onClick={() => handleSnippetTypeChange(type.value)}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  snippetType === type.value
                    ? "border-vault-600 bg-vault-50"
                    : "border-vault-200 hover:border-vault-300 bg-white"
                }`}
              >
                <div className="flex items-center gap-3">
                  {type.value === "image" ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-vault-600">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-vault-600">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                    </svg>
                  )}
                  <div>
                    <p className="font-medium text-vault-900">{type.label}</p>
                    <p className="text-xs text-vault-500">{type.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Image Subtype (only if image) */}
        {snippetType === "image" && (
          <div>
            <h3 className="text-sm font-semibold text-vault-800 mb-3">
              What type of image is this?
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {IMAGE_SUBTYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setImageSubtype(type.value)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    imageSubtype === type.value
                      ? "border-vault-600 bg-vault-50"
                      : "border-vault-200 hover:border-vault-300 bg-white"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-vault-600">{getImageSubtypeIcon(type.value)}</span>
                    <div>
                      <p className="font-medium text-vault-900 text-sm">{type.label}</p>
                      <p className="text-xs text-vault-500">{type.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Text Type (only if document) */}
        {snippetType === "document" && (
          <div>
            <h3 className="text-sm font-semibold text-vault-800 mb-3">
              What type of text does it contain?
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {TEXT_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setTextType(type.value)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    textType === type.value
                      ? "border-vault-600 bg-vault-50"
                      : "border-vault-200 hover:border-vault-300 bg-white"
                  }`}
                >
                  <p className="font-medium text-vault-900 text-sm">{type.label}</p>
                  <p className="text-xs text-vault-500">{type.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Record Subtype (only if document) */}
        {snippetType === "document" && (
          <div>
            <h3 className="text-sm font-semibold text-vault-800 mb-3">
              What type of record is this?
            </h3>
            <select
              value={recordSubtype || ""}
              onChange={(e) => setRecordSubtype(e.target.value || null)}
              className="w-full px-4 py-2 rounded-lg border border-vault-300 bg-white text-vault-900 focus:border-vault-500 focus:ring-1 focus:ring-vault-500"
            >
              <option value="">Select a record type (optional)</option>
              {recordSubtypes.map((subtype) => (
                <option key={subtype.value} value={subtype.value}>
                  {subtype.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Help text */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-800 text-sm mb-1">
            Why classify?
          </h4>
          <p className="text-blue-700 text-sm">
            {snippetType === "image" 
              ? "Classification helps organize your photos and enables features like face tagging for person photos."
              : snippetType === "document"
              ? "Classification helps organize your documents and prepares them for future text extraction features."
              : "Classification helps organize your media library and enables better search."}
          </p>
        </div>

        {/* Validation message */}
        {!snippetType && (
          <p className="text-amber-600 text-sm">
            Please select whether this is an image or document to continue.
          </p>
        )}
      </div>
    </div>
  );
}

export default StepClassification;
